/**
 * Memory Service
 * Handles user memory items and blocked topics
 */

import { supabase } from '../lib/supabase';

class MemoryService {
  /**
   * Get all memory items for a user
   */
  async getMemoryItems(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('memory_items')
        .select('*')
        .eq('user_id', userId)
        .order('is_pinned', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching memory items:', error);
      throw error;
    }
  }

  /**
   * Create a new memory item
   */
  async createMemoryItem(userId, item) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('memory_items')
        .insert([{
          user_id: userId,
          ...item
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error creating memory item:', error);
      throw error;
    }
  }

  /**
   * Update a memory item
   */
  async updateMemoryItem(itemId, updates) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('memory_items')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', itemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error updating memory item:', error);
      throw error;
    }
  }

  /**
   * Delete a memory item
   */
  async deleteMemoryItem(itemId) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('memory_items')
        .delete()
        .eq('id', itemId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting memory item:', error);
      throw error;
    }
  }

  /**
   * Toggle pin status of a memory item
   */
  async togglePinMemoryItem(itemId, isPinned) {
    try {
      return await this.updateMemoryItem(itemId, { is_pinned: isPinned });
    } catch (error) {
      console.error('Error toggling pin status:', error);
      throw error;
    }
  }

  /**
   * Clear all memory items for a user
   */
  async clearAllMemory(userId) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('memory_items')
        .delete()
        .eq('user_id', userId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error clearing all memory:', error);
      throw error;
    }
  }

  /**
   * Get blocked topics for a user
   */
  async getBlockedTopics(userId) {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('blocked_topics')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching blocked topics:', error);
      throw error;
    }
  }

  /**
   * Add a blocked topic
   */
  async addBlockedTopic(userId, topic, reason = '') {
    try {
      const { data, error } = await supabase
        .schema('public')
        .from('blocked_topics')
        .insert([{
          user_id: userId,
          topic,
          reason
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error adding blocked topic:', error);
      throw error;
    }
  }

  /**
   * Remove a blocked topic
   */
  async removeBlockedTopic(topicId) {
    try {
      const { error } = await supabase
        .schema('public')
        .from('blocked_topics')
        .delete()
        .eq('id', topicId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error removing blocked topic:', error);
      throw error;
    }
  }
}

export default new MemoryService();

